import { TiaPatientIdSystem } from '@asktia/tia-medplum-core'
import { MedplumClient } from '@medplum/core'
import {
    Bundle,
    Observation,
    DiagnosticReport,
    ServiceRequest
} from '@medplum/fhirtypes'
import { useMedplumContext } from '@medplum/react'
import { QueryKey, useQuery, UseQueryOptions } from 'react-query'
import { useUserInfo } from 'src/hooks'
import { UserInfo } from 'src/types'

function getNextPageParams(bundle?: Bundle<Observation>) {
    const url = bundle?.link?.find(link => link.relation === 'next')?.url
    if (url) {
        return new URL(url).searchParams
    }
}

function getLabTestResultHistory(
    medplum: MedplumClient,
    testCode: string,
    user?: UserInfo,
    pageSize = 1000
) {
    return async () => {
        if (!user?.id) {
            return []
        }
        const params = new URLSearchParams()
        // Filters
        params.append('code', testCode)
        params.append(
            'patient.identifier',
            `${TiaPatientIdSystem.system}|${user.id}`
        )
        params.append('has-member:missing', 'true')
        params.append('_filter', 'status eq "final" or status eq "amended"')
        // Select fields
        params.append(
            '_elements',
            'id,effectiveDateTime,status,code,valueQuantity,valueCodeableConcept,valueString,valueBoolean,valueInteger,valueRange,valueRatio,valueSampledData,valueTime,valueDateTime,valuePeriod,referenceRange,interpretation'
        )
        params.append('_count', `${pageSize}`)

        const observations = await medplum.searchResources(
            'Observation',
            params
        )

        let allResults: Observation[] = [...observations]

        let nextPageParams = getNextPageParams(observations?.bundle)
        while (nextPageParams) {
            const nextPageResults = await medplum.searchResources(
                'Observation',
                nextPageParams
            )
            allResults = [...allResults, ...nextPageResults]
            nextPageParams = getNextPageParams(nextPageResults?.bundle)
        }

        // Sort results by date (oldest to newest)
        const sortedResults = [...allResults].sort(
            (a, b) =>
                new Date(a.effectiveDateTime || '').getTime() -
                new Date(b.effectiveDateTime || '').getTime()
        )

        return sortedResults
    }
}

export type LabResultsData = {
    observation: Observation
    diagnosticReport?: DiagnosticReport
    serviceRequest?: ServiceRequest
}

export const useGetLabTestResultHistory = (
    testCode: string,
    options?: Omit<
        UseQueryOptions<Observation[], unknown, Observation[], QueryKey>,
        'queryFn'
    >
) => {
    const { medplum, loading } = useMedplumContext()
    const { user, isLoading: userIsLoading } = useUserInfo()

    const { data: testResultHistory, isLoading: labResultLoading } = useQuery<
        Observation[]
    >(
        ['get-lab-test-result-history', testCode, user?.id],
        getLabTestResultHistory(medplum, testCode, user),
        {
            enabled: !!testCode && !!user?.id,
            refetchOnWindowFocus: false,
            ...(options ?? {})
        }
    )

    return {
        isLoading: userIsLoading || labResultLoading || loading,
        testResultHistory: testResultHistory || []
    }
}
